/**
 * Karta produktu — single home for the computed readouts of the product card
 * and list (owner decision 2026-09-23,
 * docs/decisions/2026-09-23-products-tab-pallet-model.md).
 *
 * Everything arrives on pallets: sztuka → (paczka, optional) → paleta.
 * - Piece geometry reuses `resolveDimensionGeometryMm` (the same plane/depth
 *   rule as orders, PZ/WZ and transport).
 * - One pallet's LDM reuses `calculatePackagingLdm` with the pallet as the
 *   loading unit — the same projection the backend feeds to transport LDM
 *   (backend/src/db/repositories/productLogisticsSql.js).
 * Missing data stays null; nothing is guessed.
 */
import { resolveDimensionGeometryMm } from '../../utils/dimensionConversion';
import {
  calculatePackagingLdm,
  TRAILER_CAPACITY_LDM,
  TRAILER_PAYLOAD_KG,
} from '../../utils/transportCostAllocation';

export type AverageCostUnit = 'pcs' | 'm2' | 'm3';

export interface ProductLogisticsInput {
  length?: number | null;
  width?: number | null;
  thickness?: number | null;
  height?: number | null;
  /** Weight of one piece in kg. */
  grossWeight?: number | null;
  piecesPerPackage?: number | null;
  palletCountsPackages?: boolean | null;
  palletUnitsPerPallet?: number | null;
  palletLength?: number | null;
  palletWidth?: number | null;
  palletEmptyWeight?: number | null;
  palletStackLayers?: number | null;
}

export type PalletPresetId = 'eur' | 'industrial' | 'custom';

export const PALLET_PRESETS: ReadonlyArray<{
  id: PalletPresetId;
  lengthMm: number | null;
  widthMm: number | null;
  emptyWeightKg: number | null;
}> = [
  { id: 'eur', lengthMm: 1200, widthMm: 800, emptyWeightKg: 25 },
  { id: 'industrial', lengthMm: 1200, widthMm: 1000, emptyWeightKg: 30 },
  { id: 'custom', lengthMm: null, widthMm: null, emptyWeightKg: null },
];

/** A heavier pallet is flagged for a check (forklift / floor load). */
export const HEAVY_PALLET_KG = 2000;
/** Tolerance before a piece counts as longer than its pallet. */
const PIECE_OVERHANG_TOLERANCE_MM = 50;

export type PalletWarning = 'wider-than-trailer' | 'heavier-than-2t' | 'piece-longer-than-pallet';
export type ProductDataGap = 'weight' | 'pallet' | 'cost';

const positive = (value: unknown): number | null => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(numeric) && numeric > 0
    ? numeric
    : null;
};

export function matchPalletPreset(lengthMm?: number | null, widthMm?: number | null): PalletPresetId {
  const length = positive(lengthMm);
  const width = positive(widthMm);
  if (!length || !width) return 'custom';
  const preset = PALLET_PRESETS.find((candidate) => candidate.lengthMm && candidate.widthMm && (
    (candidate.lengthMm === length && candidate.widthMm === width)
    || (candidate.lengthMm === width && candidate.widthMm === length)
  ));
  return preset?.id ?? 'custom';
}

export interface PieceMetrics {
  areaM2: number | null;
  volumeM3: number | null;
  piecesPerM2: number | null;
  kgPerM2: number | null;
  weightKg: number | null;
}

export function calculatePieceMetrics(input: ProductLogisticsInput): PieceMetrics {
  const geometry = resolveDimensionGeometryMm({
    length: positive(input.length) ?? undefined,
    width: positive(input.width) ?? undefined,
    height: positive(input.height) ?? undefined,
    thickness: positive(input.thickness) ?? undefined,
  });
  const areaM2 = geometry.areaPerPieceM2 && geometry.areaPerPieceM2 > 0 ? geometry.areaPerPieceM2 : null;
  const volumeM3 = geometry.volumePerPieceM3 && geometry.volumePerPieceM3 > 0 ? geometry.volumePerPieceM3 : null;
  const weightKg = positive(input.grossWeight);
  return {
    areaM2,
    volumeM3,
    piecesPerM2: areaM2 ? 1 / areaM2 : null,
    kgPerM2: areaM2 && weightKg ? weightKg / areaM2 : null,
    weightKg,
  };
}

export interface PalletSummary {
  /** Pieces on one pallet. */
  pieces: number | null;
  /** Packages on one pallet (only when the pallet counts packages). */
  packages: number | null;
  areaM2: number | null;
  volumeM3: number | null;
  /** Gross kg: pieces × piece weight + empty pallet (0 when unknown). */
  grossKg: number | null;
  /** Trailer loading metres one pallet takes (stacking already divided). */
  ldm: number | null;
  /** Full trailer (13,6 m / 24 t): how many pallets fit. */
  palletsPerTrailer: number | null;
  limitedBy: 'weight' | 'length' | null;
  warnings: PalletWarning[];
}

export function calculatePalletSummary(input: ProductLogisticsInput): PalletSummary {
  const piece = calculatePieceMetrics(input);
  const units = positive(input.palletUnitsPerPallet);
  const perPackage = positive(input.piecesPerPackage);
  const countsPackages = input.palletCountsPackages === true;
  const pieces = units ? (countsPackages ? (perPackage ? units * perPackage : null) : units) : null;
  const packages = countsPackages && units ? units : null;
  const palletLength = positive(input.palletLength);
  const palletWidth = positive(input.palletWidth);
  const layers = positive(input.palletStackLayers);

  let ldm: number | null = null;
  let tooWide = false;
  if (pieces && palletLength && palletWidth) {
    // One pallet = one loading unit: the same function transport LDM uses.
    const result = calculatePackagingLdm({
      quantity: pieces,
      unit: 'pcs',
      piecesPerPackage: pieces,
      packageLengthMm: palletLength,
      packageWidthMm: palletWidth,
      packageStackLayers: layers ?? 1,
    });
    ldm = result.ldm;
    tooWide = result.code === 'PACKAGE_EXCEEDS_TRAILER_WIDTH';
  }

  const grossKg = pieces && piece.weightKg
    ? pieces * piece.weightKg + (positive(input.palletEmptyWeight) ?? 0)
    : null;

  let palletsPerTrailer: number | null = null;
  let limitedBy: PalletSummary['limitedBy'] = null;
  if (ldm && ldm > 0) {
    const byLength = Math.floor(TRAILER_CAPACITY_LDM / ldm + 1e-9);
    const byWeight = grossKg ? Math.floor(TRAILER_PAYLOAD_KG / grossKg) : Number.POSITIVE_INFINITY;
    palletsPerTrailer = Math.min(byLength, byWeight);
    limitedBy = byWeight < byLength ? 'weight' : 'length';
  }

  const warnings: PalletWarning[] = [];
  if (tooWide) warnings.push('wider-than-trailer');
  if (grossKg && grossKg > HEAVY_PALLET_KG) warnings.push('heavier-than-2t');
  const pieceLongest = Math.max(positive(input.length) ?? 0, positive(input.width) ?? 0);
  if (pieceLongest > 0 && palletLength && palletWidth
    && pieceLongest > Math.max(palletLength, palletWidth) + PIECE_OVERHANG_TOLERANCE_MM) {
    warnings.push('piece-longer-than-pallet');
  }

  return {
    pieces,
    packages,
    areaM2: pieces && piece.areaM2 ? pieces * piece.areaM2 : null,
    volumeM3: pieces && piece.volumeM3 ? pieces * piece.volumeM3 : null,
    grossKg,
    ldm,
    palletsPerTrailer,
    limitedBy,
    warnings,
  };
}

export interface AverageCostConversions {
  perPiece: number | null;
  perM2: number | null;
  perM3: number | null;
  perPallet: number | null;
}

/** Re-expresses the average cost (PLN net in its unit) in the other units. */
export function convertAverageCost(
  price: number | null | undefined,
  unit: AverageCostUnit | null | undefined,
  input: ProductLogisticsInput,
): AverageCostConversions {
  const empty = { perPiece: null, perM2: null, perM3: null, perPallet: null };
  if (price === null || price === undefined || !Number.isFinite(Number(price)) || !unit) return empty;
  const piece = calculatePieceMetrics(input);
  const value = Number(price);
  const perPiece = unit === 'pcs'
    ? value
    : unit === 'm2'
      ? (piece.areaM2 ? value * piece.areaM2 : null)
      : (piece.volumeM3 ? value * piece.volumeM3 : null);
  if (perPiece === null) return { ...empty, [unit === 'm2' ? 'perM2' : 'perM3']: value };
  const pallet = calculatePalletSummary(input);
  return {
    perPiece,
    perM2: piece.areaM2 ? perPiece / piece.areaM2 : null,
    perM3: piece.volumeM3 ? perPiece / piece.volumeM3 : null,
    perPallet: pallet.pieces ? perPiece * pallet.pieces : null,
  };
}

/**
 * What the list's "Dane" column and the "Niekompletne" filter report.
 * `includeCost` is false for a viewer who cannot see the average cost —
 * redacted data is not the same as missing data.
 */
export function getProductDataGaps(
  product: ProductLogisticsInput & { averagePurchasePricePLN?: number | null },
  { includeCost }: { includeCost: boolean },
): ProductDataGap[] {
  const gaps: ProductDataGap[] = [];
  if (!positive(product.grossWeight)) gaps.push('weight');
  const pallet = calculatePalletSummary(product);
  if (!pallet.pieces || !pallet.ldm) gaps.push('pallet');
  if (includeCost && (product.averagePurchasePricePLN === null || product.averagePurchasePricePLN === undefined)) {
    gaps.push('cost');
  }
  return gaps;
}

/**
 * Fields a pallet preset writes into the form: "pod wymiar towaru" follows the
 * piece footprint, EUR/industrial set their size and empty weight.
 */
export function palletPresetFieldValues(
  presetId: PalletPresetId,
  current: ProductLogisticsInput,
): Pick<ProductLogisticsInput, 'palletLength' | 'palletWidth' | 'palletEmptyWeight'> | null {
  const chosen = PALLET_PRESETS.find((candidate) => candidate.id === presetId);
  if (!chosen) return null;
  if (presetId === 'custom') {
    return {
      palletLength: current.length ?? current.palletLength ?? null,
      palletWidth: current.width ?? current.palletWidth ?? null,
    };
  }
  return {
    palletLength: chosen.lengthMm,
    palletWidth: chosen.widthMm,
    palletEmptyWeight: chosen.emptyWeightKg,
  };
}

/**
 * Form values of the logistics set shared by the product card and the variant
 * editor (a real variant owns the same set — owner decision 2026-09-24,
 * docs/decisions/2026-09-24-wariant-wlasne-dane-logistyczne.md). A new variant
 * is prefilled from its product through this same mapping.
 */
export function toLogisticsFormValues(
  source: ProductLogisticsInput | null | undefined,
): Omit<ProductLogisticsInput, 'palletCountsPackages'> & { palletCountsPackages: boolean } {
  const countsPackages = source?.palletCountsPackages === true;
  return {
    length: source?.length ?? null,
    width: source?.width ?? null,
    thickness: source?.thickness ?? null,
    // "height" stays editable only where it is already used as a plane side.
    ...(Number(source?.height) > 0 ? { height: source?.height } : {}),
    grossWeight: source?.grossWeight ?? null,
    palletCountsPackages: countsPackages,
    // A loose pallet's legacy paczka value is not shown and is cleared on save.
    piecesPerPackage: countsPackages ? source?.piecesPerPackage ?? null : null,
    palletUnitsPerPallet: source?.palletUnitsPerPallet ?? null,
    palletLength: source?.palletLength ?? null,
    palletWidth: source?.palletWidth ?? null,
    palletEmptyWeight: source?.palletEmptyWeight ?? null,
    palletStackLayers: source?.palletStackLayers ?? null,
  };
}
